import { useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { PostureManagementTabType } from '@/components/Inventory/types';
import { PostureManagementTabs } from '@/components/Inventory/constants';

interface InventoryTabDetails {
  label: string;
  guard?: () => boolean;
}

interface UseInventoryTabManagementOptions {
  tabsConfig: Record<PostureManagementTabType, InventoryTabDetails>;
}

interface UseInventoryTabManagementReturn {
  currentTab: string;
  handleTabChange: (newTab: string) => void;
}

/**
 * Gets the initial tab from URL or defaults to first visible tab
 */
const getInitialTab = (
  tabsConfig: Record<PostureManagementTabType, InventoryTabDetails>,
  urlTab?: string | null
): string => {
  // Legacy redirect: ?tab=user-security-posture → ?tab=users
  const resolvedTab =
    urlTab === 'user-security-posture'
      ? PostureManagementTabs.USER_SECURITY_POSTURE
      : urlTab;

  // If URL contains a tab parameter, validate it
  if (resolvedTab) {
    const config = tabsConfig[resolvedTab as PostureManagementTabType];
    const isValidTab = config && (config.guard?.() ?? true);
    if (isValidTab) {
      return resolvedTab;
    }
  }

  // Fallback to first visible tab
  const tabOrder = [
    PostureManagementTabs.USER_SECURITY_POSTURE,
    PostureManagementTabs.PROJECTS,
    PostureManagementTabs.MCP,
    PostureManagementTabs.EXTENSIONS,
    PostureManagementTabs.AI_INSTRUCTIONS,
  ];

  for (const tabKey of tabOrder) {
    const config = tabsConfig[tabKey];
    const showTab = config?.guard?.() ?? true;
    if (showTab) {
      return tabKey;
    }
  }

  // Fallback - should not happen if at least one tab is visible
  return PostureManagementTabs.MCP;
};

/**
 * Custom hook to manage Inventory tab state and URL synchronization
 *
 * This hook handles:
 * - Reading the active tab from URL query parameters
 * - Syncing tab state with URL (with fallback for invalid tabs)
 * - Updating URL when tab changes
 */
export const useInventoryTabManagement = ({
  tabsConfig,
}: UseInventoryTabManagementOptions): UseInventoryTabManagementReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get tab from URL or default to first visible tab
  // Use the URL as the source of truth
  const urlTab = searchParams.get('tab');
  const currentTab = useMemo(
    () => getInitialTab(tabsConfig, urlTab),
    [tabsConfig, urlTab]
  );

  // Sync URL with the initial tab if no tab or invalid tab in URL
  useEffect(() => {
    const needsUrlUpdate = !urlTab || urlTab !== currentTab;

    if (needsUrlUpdate) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('tab', currentTab);
      setSearchParams(newParams, { replace: true });
    }
  }, [currentTab, urlTab, searchParams, setSearchParams]);

  // Handler for tab changes - update URL
  const handleTabChange = (newTab: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', newTab);
    setSearchParams(newParams, { replace: true });
  };

  return {
    currentTab,
    handleTabChange,
  };
};
